// ============================================================================
// CONFIG SETTINGS SCREEN
// ============================================================================
// Main screen for the Configuration Settings module.
// Uses the useConfigSettings hook for all data and state management.
// Guards all navigation/scope changes with an unsaved-changes confirmation
// dialog so the user never accidentally loses pending edits.

import React, { useState, useCallback, useRef } from 'react';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useConfigSettings } from 'hooks/api/useConfigSettings';
import type { ScopeType } from 'hooks/api/useConfigSettings';
import ConfigGroupSidebar from './ConfigGroupSidebar';
import ConfigSettingsPanel from './ConfigSettingsPanel';
import './config-module.css';

interface ConfigSettingsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ConfigSettingsScreen: React.FC<ConfigSettingsScreenProps> = () => {
  const {
    groups,
    groupsLoading,
    activeGroupKey,
    scopeType,
    selectedOrganizationId,
    selectedDepartmentId,
    effectiveValues,
    definitions,
    editedValues,
    hasChanges,
    loading,
    saving,
    selectGroup,
    changeScopeType,
    selectOrganization,
    selectDepartment,
    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  } = useConfigSettings();

  // Pending navigation callback — executed after the user confirms discard
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const activeGroup = groups.find((g) => g.groupKey === activeGroupKey) ?? null;

  // ── Guard helpers ──────────────────────────────────────────────────────────

  const guardedAction = useCallback(
    (action: () => void) => {
      if (hasChanges) {
        pendingActionRef.current = action;
        setConfirmOpen(true);
      } else {
        action();
      }
    },
    [hasChanges]
  );

  const handleGroupSelect = useCallback(
    (groupKey: string) => {
      if (groupKey === activeGroupKey) return;
      guardedAction(() => selectGroup(groupKey));
    },
    [activeGroupKey, guardedAction, selectGroup]
  );

  const handleScopeTypeChange = useCallback(
    (newScope: ScopeType) => {
      if (newScope === scopeType) return;
      guardedAction(() => changeScopeType(newScope));
    },
    [scopeType, guardedAction, changeScopeType]
  );

  const handleOrganizationSelect = useCallback(
    (orgId: string | null) => {
      if (orgId === selectedOrganizationId) return;
      guardedAction(() => selectOrganization(orgId));
    },
    [selectedOrganizationId, guardedAction, selectOrganization]
  );

  const handleDepartmentSelect = useCallback(
    (deptId: string | null) => {
      if (deptId === selectedDepartmentId) return;
      guardedAction(() => selectDepartment(deptId));
    },
    [selectedDepartmentId, guardedAction, selectDepartment]
  );

  // ── Unsaved-changes navigation confirm ────────────────────────────────────

  const handleConfirmDiscard = useCallback(() => {
    discardChanges();
    setConfirmOpen(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, [discardChanges]);

  const handleCancelDiscard = useCallback(() => {
    setConfirmOpen(false);
    pendingActionRef.current = null;
  }, []);

  // ── Save confirmation ──────────────────────────────────────────────────────

  const handleSaveClick = useCallback(() => setSaveConfirmOpen(true), []);

  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    saveChanges();
  }, [saveChanges]);

  const handleCancelSave = useCallback(() => setSaveConfirmOpen(false), []);

  // ── Discard confirmation ───────────────────────────────────────────────────

  const handleDiscardClick = useCallback(() => setDiscardConfirmOpen(true), []);

  const handleConfirmDiscardChanges = useCallback(() => {
    setDiscardConfirmOpen(false);
    discardChanges();
  }, [discardChanges]);

  const handleCancelDiscardChanges = useCallback(() => setDiscardConfirmOpen(false), []);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="module-master-container">
      <div className="config-settings-container">
        <ConfigGroupSidebar
          groups={groups}
          activeGroupKey={activeGroupKey}
          onGroupSelect={handleGroupSelect}
          loading={groupsLoading}
        />
        <ConfigSettingsPanel
          activeGroup={activeGroup}
          scopeType={scopeType}
          onScopeTypeChange={handleScopeTypeChange}
          selectedOrganizationId={selectedOrganizationId}
          onOrganizationSelect={handleOrganizationSelect}
          selectedDepartmentId={selectedDepartmentId}
          onDepartmentSelect={handleDepartmentSelect}
          effectiveValues={effectiveValues}
          definitions={definitions}
          editedValues={editedValues}
          onValueChange={updateValue}
          onResetValue={resetValue}
          onSave={handleSaveClick}
          onDiscard={handleDiscardClick}
          hasChanges={hasChanges}
          saving={saving}
          loading={loading}
        />
      </div>

      {/* Navigation-guard dialog (group/scope/context change with unsaved changes) */}
      <ConfirmDialog
        open={confirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard them and continue?"
        confirmLabel="Discard & Continue"
        cancelLabel="Keep Editing"
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
        variant="danger"
      />

      {/* Explicit save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Configuration"
        message="Are you sure you want to save these configuration changes?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={handleCancelSave}
        variant="success"
      />

      {/* Explicit discard confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes"
        message="Are you sure you want to discard all unsaved changes?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDiscardChanges}
        onCancel={handleCancelDiscardChanges}
        variant="danger"
      />
    </div>
  );
};

export default ConfigSettingsScreen;
